// NDBC buoy-observations data layer for SeaState.
//
// Fetches the NDBC "realtime2" standard-met file for a buoy through the Go
// server's same-origin relay (`/ndbc/data/realtime2/<ID>.txt`) and parses it
// into typed, ascending-by-time arrays. This module does NO rendering and
// touches NO DOM — it is data in, data out.
//
// Like the rest of the forecast layer, it degrades gracefully: any failure —
// network, a short/garbage file, an unrecognisable header — yields `null`
// rather than throwing into a blank page. (Ported from the v1 `fetchBuoyObs` /
// `bucketObs`, with the column layout now read from the header by NAME.)
//
// File format (verified against /ndbc/data/realtime2/42035.txt):
//   #YY  MM DD hh mm WDIR WSPD GST  WVHT   DPD   APD MWD   PRES ...   ← names
//   #yr  mo dy hr mn degT m/s  m/s     m   sec   sec degT   hPa ...   ← units
//   2026 07 11 05 50 130  3.0  4.0   0.7     5   4.2 174 1016.5 ...   ← data
// Both header lines start with "#"; data rows are whitespace-separated, NEWEST
// FIRST, timestamps UTC, and missing values are the sentinel "MM". WSPD/GST are
// m/s (converted here to knots), WVHT is metres, DPD is seconds.

export interface BuoyObs {
  /** UTC epoch ms per observation row, ascending. */
  timesUtcMs: number[];
  wspdKn: (number | null)[];
  gstKn: (number | null)[];
  wvhtM: (number | null)[];
  dpdS: (number | null)[];
  /** Visibility, nautical miles (NDBC VIS column). Reported mainly by C-MAN /
   *  Marine-METAR stations; null where the station carries no visibility sensor. */
  visNmi: (number | null)[];
  /** Wind direction, degrees true (WDIR). */
  wdirDeg: (number | null)[];
  /** Sea-level pressure, hPa (PRES). */
  presHpa: (number | null)[];
  /** Air temperature, °C (ATMP). */
  atmpC: (number | null)[];
  /** Water temperature, °C (WTMP). */
  wtmpC: (number | null)[];
}

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;
const MPS_TO_KN = 1.94384;

/** Parse one whitespace field: the "MM" sentinel or a non-finite value → null. */
function field(value: string | undefined): number | null {
  if (value === undefined || value === "MM") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Map a header NAMES line ("#YY MM DD hh mm WDIR WSPD GST WVHT DPD ...") to a
 * column-name → index lookup. The leading "#" is stripped and, defensively,
 * any "#" fused to the first token ("#YY"). Returns null if the line is empty.
 */
function headerIndex(headerLine: string): Map<string, number> | null {
  const names = headerLine
    .replace(/^#/, "")
    .trim()
    .split(/\s+/)
    .map((name) => name.replace(/^#/, ""));
  if (names.length === 0 || names[0] === "") return null;
  const at = new Map<string, number>();
  names.forEach((name, i) => {
    if (!at.has(name)) at.set(name, i);
  });
  return at;
}

/**
 * Parse the full text of a realtime2 standard-met file into ascending BuoyObs.
 * Returns null on any structural problem (no header, missing time or measured
 * columns, no usable rows). Rows older than 4 days before the newest row are
 * dropped.
 */
export function parseBuoyObs(text: string): BuoyObs | null {
  const lines = text.split("\n");

  // The first "#"-prefixed line carries the column NAMES.
  let headerLine: string | null = null;
  const dataLines: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") continue;
    if (line.startsWith("#")) {
      if (headerLine === null) headerLine = line;
      continue;
    }
    dataLines.push(line);
  }
  if (headerLine === null || dataLines.length === 0) return null;

  const col = headerIndex(headerLine);
  if (!col) return null;

  const yy = col.get("YY");
  const mm = col.get("MM");
  const dd = col.get("DD");
  const hh = col.get("hh");
  const mn = col.get("mm");
  const wspd = col.get("WSPD");
  const gst = col.get("GST");
  const wvht = col.get("WVHT");
  const dpd = col.get("DPD");
  // Extra standard-met columns present in the same realtime2 file — collected so
  // a station's fuller read (visibility especially) isn't thrown away.
  const vis = col.get("VIS");
  const wdir = col.get("WDIR");
  const pres = col.get("PRES");
  const atmp = col.get("ATMP");
  const wtmp = col.get("WTMP");
  // Time columns are mandatory; the measured columns are read when present.
  if (yy == null || mm == null || dd == null || hh == null || mn == null) {
    return null;
  }
  if (wspd == null && gst == null && wvht == null && dpd == null && vis == null) return null;

  interface Row {
    t: number;
    wspdKn: number | null;
    gstKn: number | null;
    wvhtM: number | null;
    dpdS: number | null;
    visNmi: number | null;
    wdirDeg: number | null;
    presHpa: number | null;
    atmpC: number | null;
    wtmpC: number | null;
  }
  const rows: Row[] = [];
  for (const line of dataLines) {
    const c = line.split(/\s+/);
    const year = field(c[yy]);
    const month = field(c[mm]);
    const day = field(c[dd]);
    const hour = field(c[hh]);
    const minute = field(c[mn]);
    if (year == null || month == null || day == null || hour == null || minute == null) {
      continue;
    }
    const t = Date.UTC(year, month - 1, day, hour, minute);
    if (!Number.isFinite(t)) continue;
    const wsRaw = wspd == null ? null : field(c[wspd]);
    const gsRaw = gst == null ? null : field(c[gst]);
    rows.push({
      t,
      wspdKn: wsRaw == null ? null : wsRaw * MPS_TO_KN,
      gstKn: gsRaw == null ? null : gsRaw * MPS_TO_KN,
      wvhtM: wvht == null ? null : field(c[wvht]),
      dpdS: dpd == null ? null : field(c[dpd]),
      visNmi: vis == null ? null : field(c[vis]),
      wdirDeg: wdir == null ? null : field(c[wdir]),
      presHpa: pres == null ? null : field(c[pres]),
      atmpC: atmp == null ? null : field(c[atmp]),
      wtmpC: wtmp == null ? null : field(c[wtmp]),
    });
  }
  if (rows.length === 0) return null;

  // Sort ascending by time, then keep only ~the last 4 days before the newest.
  rows.sort((a, b) => a.t - b.t);
  const newest = rows[rows.length - 1].t;
  const cutoff = newest - 4 * MS_PER_DAY;
  const kept = rows.filter((r) => r.t >= cutoff);

  return {
    timesUtcMs: kept.map((r) => r.t),
    wspdKn: kept.map((r) => r.wspdKn),
    gstKn: kept.map((r) => r.gstKn),
    wvhtM: kept.map((r) => r.wvhtM),
    dpdS: kept.map((r) => r.dpdS),
    visNmi: kept.map((r) => r.visNmi),
    wdirDeg: kept.map((r) => r.wdirDeg),
    presHpa: kept.map((r) => r.presHpa),
    atmpC: kept.map((r) => r.atmpC),
    wtmpC: kept.map((r) => r.wtmpC),
  };
}

/**
 * The relay path for a station's standard-met file.
 *
 * UPPERCASED, and that is the whole point of this function existing. NDBC's
 * realtime2 filenames are uppercase — /data/realtime2/KVAF.txt — but the
 * activestations.xml catalog we build the map from publishes every id in LOWER
 * case. Of the ~1,350 met stations in that catalog, 609 are numeric (case makes
 * no difference) and 742 carry letters, so more than half the buoys on the map
 * were requesting a filename that does not exist. NDBC answered 404, the relay
 * turned that into 502, fetchBuoyObs swallowed it as null, and the station
 * simply drew no observation dots — no error anywhere, just a buoy that looked
 * like it had nothing to say. Numeric stations worked, which is exactly why it
 * read as "only some buoys work".
 *
 * Display is unaffected: the lowercase id is what NDBC's own station_page.php
 * accepts, so stationUrl and the labels keep using the id as catalogued.
 */
export function buoyObsPath(id: string): string {
  return `/ndbc/data/realtime2/${encodeURIComponent(id.toUpperCase())}.txt`;
}

/**
 * Fetch + parse a station's standard-met file through the same-origin relay.
 * Returns null on any failure. Keeps ~the last 4 days of observations.
 */
export async function fetchBuoyObs(id: string): Promise<BuoyObs | null> {
  try {
    const res = await fetch(buoyObsPath(id));
    if (!res.ok) return null;
    const text = await res.text();
    return parseBuoyObs(text);
  } catch {
    return null;
  }
}

/**
 * Average observations into the forecast's hourly buckets. `times` are the
 * forecast's LOCAL hour labels ("2026-07-11T14:00"); a label's UTC hour start
 * is `Date.parse(label + ":00Z") - utcOffsetSeconds*1000`. Each obs is placed
 * in the nearest hour bucket and the buckets are averaged. Hours with no obs —
 * or with no finite obs for the chosen field — are null.
 */
export function bucketObs(
  obs: BuoyObs,
  field: "wspdKn" | "gstKn" | "wvhtM" | "dpdS" | "visNmi" | "wdirDeg" | "presHpa" | "atmpC" | "wtmpC",
  times: string[],
  utcOffsetSeconds: number,
): (number | null)[] {
  const out: (number | null)[] = new Array<number | null>(times.length).fill(null);
  if (times.length === 0) return out;

  // UTC epoch of the first label's hour start; the labels are contiguous hours.
  const t0 = Date.parse(times[0] + ":00Z") - utcOffsetSeconds * 1000;
  if (Number.isNaN(t0)) return out;

  const sum = new Array<number>(times.length).fill(0);
  const cnt = new Array<number>(times.length).fill(0);
  const values = obs[field];
  for (let r = 0; r < obs.timesUtcMs.length; r++) {
    const v = values[r];
    if (v == null || !Number.isFinite(v)) continue;
    const i = Math.round((obs.timesUtcMs[r] - t0) / MS_PER_HOUR);
    if (i >= 0 && i < times.length) {
      sum[i] += v;
      cnt[i]++;
    }
  }
  for (let i = 0; i < times.length; i++) {
    if (cnt[i] > 0) out[i] = sum[i] / cnt[i];
  }
  return out;
}

/**
 * Mean of (multi-model mean − observation) over hour indexes [fromIdx, toIdx].
 * For each hour, the model mean is the mean of the finite model values; the
 * hour contributes only when the observation and at least one model value are
 * finite. Returns null when no such hour exists in range. (Ported from the v1
 * model-minus-buoy bias.)
 */
export function meanBias(
  models: (number | null)[][],
  obsBucketed: (number | null)[],
  fromIdx: number,
  toIdx: number,
): number | null {
  const lo = Math.max(0, fromIdx);
  const hi = Math.min(obsBucketed.length - 1, toIdx);
  let sumError = 0;
  let n = 0;
  for (let i = lo; i <= hi; i++) {
    const o = obsBucketed[i];
    if (o == null || !Number.isFinite(o)) continue;
    let modelSum = 0;
    let modelCount = 0;
    for (const series of models) {
      const v = series[i];
      if (v != null && Number.isFinite(v)) {
        modelSum += v;
        modelCount++;
      }
    }
    if (modelCount === 0) continue;
    sumError += modelSum / modelCount - o;
    n++;
  }
  return n > 0 ? sumError / n : null;
}
